import React, { Suspense } from 'react'
import PropTypes from 'prop-types'
import { propOr } from 'ramda'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { QUERY_INPUT_TYPES } from 'utils/constants'
import { getOutputQuery } from 'selectors/feature/outputs'
import { updateSelectedInput } from 'actions/feature/inputs'
import { getShowHarnesses } from 'selectors/feature/harnesses'
import {
  getConfiguredInputs,
  getDefaultInputConfigurations,
} from 'selectors/feature/inputs'
import { makeConditionSelector } from 'selectors/feature/conditions'
import Circuit from 'components/feature/Harnesses/Circuit'
import FixedOrFloatingEditor from 'components/feature/NavigatorPanes/editors/FixedOrFloatingEditor'

import { schemaProp } from 'schemas/validation'
import conditionSchema from 'schemas/validation/feature/conditionSchema'

import ContextualHelp from 'components/ContextualHelp'
import { Flex, Paragraph } from 'components/patterns'
import strings from 'utils/strings'
import { Icon__HelpInput as IconHelpInput } from 'components/patterns/Icon/components'
import styled from '@emotion/styled'
import { StyledOutputQuery } from './OutputControlSelection.style'

const rule = schemaProp(conditionSchema)
const { CARRY, CAN_INPUT, DIGITAL_INPUT, ANALOG_INPUT } = QUERY_INPUT_TYPES

const OutputControlSelection = ({
  selectedType,
  isSingleConditionOutput,
  isNativeOutput,
  fnId,
  id,
}) => {
  const isCarry = selectedType === CARRY
  const isCanInput = selectedType === CAN_INPUT

  const dispatch = useDispatch()
  const outputQuery = useSelector(getOutputQuery, { fnId })
  const condition = useMakeSelector(makeConditionSelector, {
    queryUri: outputQuery?.uri,
  })
  const configuredInputs = useSelector(getConfiguredInputs)
  const defaultInputConfigurations = useSelector(getDefaultInputConfigurations)
  const showHarnesses = useSelector(getShowHarnesses)
  const showCircuit =
    showHarnesses &&
    (selectedType === ANALOG_INPUT || selectedType === DIGITAL_INPUT)
  const floatingCircuit = propOr(null, 'floatingCircuit')(condition)
  const signal = propOr(null, 'signal')(condition)
  const onInputChange = (updatedInputUri) => {
    dispatch(
      updateSelectedInput(
        outputQuery?.uri,
        selectedType,
        configuredInputs,
        defaultInputConfigurations,
        updatedInputUri
      )
    )
  }

  return (
    <>
      {isCanInput && <OutputControlHelper />}
      {!isCanInput && !isCarry && isNativeOutput && (
        <FixedOrFloatingEditor
          floatingValue={floatingCircuit}
          onFloatingChange={onInputChange}
          floatingActive={showCircuit}
          floatingRules={rule('floatingCircuit')}
          subType={selectedType}
        >
          <OutputControlHelper />
          {showCircuit && <Circuit id={signal} />}
        </FixedOrFloatingEditor>
      )}

      {isCarry && (
        <StyledOutputQuery
          fnId={fnId}
          id={id}
          isSingleConditionOutput={isSingleConditionOutput}
        />
      )}
    </>
  )
}

OutputControlSelection.propTypes = {
  selectedType: PropTypes.string,
  fnId: PropTypes.string,
  id: PropTypes.string,
  isSixteenBitCan: PropTypes.bool,
  isNativeOutput: PropTypes.bool,
  isSingleConditionOutput: PropTypes.bool,
}

export default OutputControlSelection

export function OutputControlHelper(props) {
  return (
    <Flex flexDirection="column">
      <Paragraph
        style={{
          color: 'rgba(108, 24, 186, 1.0',
          fontSize: 12,
          marginTop: 12,
        }}
      >{`${strings.native_output_helper_text}:`}</Paragraph>
      <StyledContextualHelp
        contentMsg={
          <Paragraph>{strings.native_output_helper_long_text}</Paragraph>
        }
        icon={
          <Suspense fallback="">
            <IconHelpInput width={20} height={20} />
          </Suspense>
        }
      />
    </Flex>
  )
}

const StyledContextualHelp = styled(ContextualHelp)`
  margin-bottom: 0;
`
